import { TagOptions } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import uuid from 'react-native-uuid';
import { generateTagColor } from '@shared/components/blocks/lib/tags/utils';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { AuditTagComponent } from '../config';
import { useWorkflowDispatch } from '../context/context';

type Component = API.AnyComponent;

export function useSetComponentField<T, U extends Component = Component>(
  component: U,
  field: keyof U,
) {
  const dispatch = useWorkflowDispatch();
  return useMemo(
    () =>
      [
        component[field],
        (newValue: U[typeof field]) => {
          dispatch({
            type: 'updateComponent',
            args: {
              ...component,
              [field]: newValue,
            },
          });
        },
      ] as [T, (newValue: T) => void],
    [dispatch, component, field],
  );
}

type TagComponent = API.TagComponent;
type TagOption = API.TagComponentOption;

interface UseTagOptionsHook {
  onAddOption: VoidFunction;
  onUpdateOption: (value: string, label: string) => void;
  onArchiveOption: (value: string) => void;
  onUnarchiveOption: (value: string) => void;
  onReorderOption: (source: number, target: number) => void;
  onSetTagGroup: (options: TagOptions['options']) => void;
  onSetDefault: (value: string[]) => void;
  onCheckToggle: VoidFunction;
  onChecklistEnabledToggle: (enabled: boolean) => void;
  canArchiveOption: boolean;
  canAddOption: boolean;
  options: TagOption[];
  archivedOptions: TagOption[];
  defaultValue: string[];
  checkedOn: string | null;
  checkedOff: string | null;
  getOverrideValue: (path: string) => string[];
  onSetOverride: (path: string, value: string[]) => void;
}

export function useTagOptions(component: TagComponent | AuditTagComponent): UseTagOptionsHook {
  const dispatch = useWorkflowDispatch();
  const tagComponent = component as TagComponent;
  const options = useMemo(() => component.options ?? [], [component.options]);
  const activeOptions = useMemo(() => options.filter(({ archived }) => !archived), [options]);
  const archivedOptions = useMemo(() => options.filter(({ archived }) => archived), [options]);
  const checkedOn =
    isTagComponent(component) && !isEmpty(component.checklistOptions?.checkedOption)
      ? component.checklistOptions?.checkedOption ?? null
      : null;
  const checkedOff =
    isTagComponent(component) && !isEmpty(component.checklistOptions?.uncheckedOption)
      ? component.checklistOptions?.uncheckedOption ?? null
      : null;

  const onChecklistEnabledToggle = useCallback(
    (enabled: boolean) => {
      // Determine the two active (non-archived) options – there should only ever be two
      const [firstOpt, secondOpt] = activeOptions;

      dispatch({
        type: 'updateComponent',
        args: {
          ...tagComponent,
          checklistEnabled: enabled,
          checklistOptions: enabled
            ? {
                checkedOption: firstOpt?.value ?? '',
                uncheckedOption: secondOpt?.value ?? '',
              }
            : null,
        },
      });
    },
    [dispatch, tagComponent, activeOptions],
  );

  const onCheckToggle = useCallback(() => {
    const existingOptions = tagComponent.checklistOptions;

    dispatch({
      type: 'updateComponent',
      args: {
        ...tagComponent,
        checklistOptions: {
          checkedOption: existingOptions?.uncheckedOption ?? '',
          uncheckedOption: existingOptions?.checkedOption ?? '',
        },
      },
    });
  }, [dispatch, tagComponent]);

  const onAddOption = useCallback(() => {
    dispatch({
      type: 'updateComponent',
      args: {
        ...tagComponent,
        options: [
          ...options,
          {
            label: '',
            value: uuid.v4(),
            backgroundColor: generateTagColor(options.length),
            archived: false,
          } as TagOption,
        ],
      },
    });
  }, [dispatch, tagComponent, options]);

  const onUpdateOption = useCallback(
    (value: string, label: string) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...tagComponent,
          options: options.map(option => {
            if (option.value === value) {
              return {
                label,
                value,
                backgroundColor: option.backgroundColor,
                archived: !isNil(option.archived) ? option.archived : false,
              };
            }

            return option;
          }),
        } as API.TagComponent,
      });
    },
    [dispatch, tagComponent, options],
  );

  const onArchiveOption = useCallback(
    (value: string) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...tagComponent,
          options: options.map(option => {
            if (option.value !== value) {
              return option;
            } else {
              return {
                ...option,
                archived: true,
              };
            }
          }),
        },
      });
    },
    [dispatch, options, tagComponent],
  );

  const onUnarchiveOption = useCallback(
    (value: string) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...tagComponent,
          options: options.map(option => {
            if (option.value !== value) {
              return option;
            } else {
              return {
                ...option,
                archived: false,
              };
            }
          }),
        } as TagComponent,
      });
    },
    [dispatch, tagComponent, options],
  );

  const onReorderOption = useCallback(
    (source: number, target: number) => {
      const newOptions = [...activeOptions];
      const [reorderedItem] = newOptions.splice(source, 1);
      newOptions.splice(target, 0, reorderedItem);

      dispatch({
        type: 'updateComponent',
        args: {
          ...tagComponent,
          options: newOptions,
        },
      });
    },
    [tagComponent, dispatch, activeOptions],
  );

  const onSetTagGroup = useCallback(
    (optionsGroup: TagOptions['options']) => {
      const existingOptions =
        tagComponent.options?.map(option => ({ ...option, archived: true })) ?? [];
      const isChecklistEnabled = tagComponent.checklistEnabled;
      const nextOptions = [
        ...existingOptions,
        ...(optionsGroup ?? []).map(option => {
          const { label, theme } = option;
          return {
            label,
            value: uuid.v4(),
            backgroundColor: theme?.backgroundColor,
            archived: false,
          };
        }),
      ];
      const nextChecklistOptions = nextOptions.filter(option => !option.archived);
      const checklistOptions = isChecklistEnabled
        ? {
            checkedOption: nextChecklistOptions[0].value,
            uncheckedOption: nextChecklistOptions[1].value,
          }
        : null;

      dispatch({
        type: 'updateComponent',
        args: {
          ...tagComponent,
          checklistOptions,
          options: nextOptions,
        } as TagComponent,
      });
    },
    [tagComponent, dispatch],
  );

  const onSetDefault = useCallback(
    (value: string[]) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...tagComponent,
          default: { value },
        },
      });
    },
    [tagComponent, dispatch],
  );

  const onSetOverride = useCallback(
    (path: string, value: string[]) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...tagComponent,
          [path]: { value },
        },
      });
    },
    [tagComponent, dispatch],
  );

  const canArchiveOption = useMemo(() => {
    return activeOptions.length > 1 && !tagComponent.checklistEnabled;
  }, [activeOptions, tagComponent.checklistEnabled]);

  const canAddOption = useMemo(
    () => !tagComponent.checklistEnabled || activeOptions.length < 2,
    [activeOptions, tagComponent.checklistEnabled],
  );

  const defaultValue = useMemo(
    () => tagComponent.default?.value ?? [],
    [tagComponent.default?.value],
  );

  const getOverrideValue = useCallback(
    (path: string) => {
      return tagComponent[path]?.value;
    },
    [tagComponent],
  );

  return useMemo(
    () => ({
      onAddOption,
      onUpdateOption,
      onArchiveOption,
      onUnarchiveOption,
      onReorderOption,
      onSetTagGroup,
      onSetDefault,
      onCheckToggle,
      onChecklistEnabledToggle,
      canArchiveOption,
      canAddOption,
      options: activeOptions,
      archivedOptions,
      checkedOn,
      checkedOff,
      defaultValue,
      getOverrideValue,
      onSetOverride,
    }),
    [
      onAddOption,
      onUpdateOption,
      onArchiveOption,
      onUnarchiveOption,
      onReorderOption,
      onSetTagGroup,
      onSetDefault,
      onCheckToggle,
      onChecklistEnabledToggle,
      canArchiveOption,
      canAddOption,
      activeOptions,
      archivedOptions,
      checkedOn,
      checkedOff,
      defaultValue,
      getOverrideValue,
      onSetOverride,
    ],
  );
}
